/**
 * @fileoverview JSON Schema helper utilities for MutagenT CLI
 * @module @mutagent/cli/lib/schema-helpers
 *
 * Provides validation, building, and formatting utilities for JSON Schema
 * objects used with prompt inputSchema/outputSchema fields.
 */

/**
 * Variable definition for building JSON Schema interactively.
 */
export interface SchemaVariable {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description: string;
}

/**
 * Supported JSON Schema primitive types for interactive variable definition.
 */
export const SUPPORTED_SCHEMA_TYPES = ['string', 'number', 'boolean', 'array', 'object'] as const;
export type SupportedSchemaType = typeof SUPPORTED_SCHEMA_TYPES[number];

/**
 * Check whether a value is a valid JSON Schema (basic structural validation).
 *
 * A valid JSON Schema for our purposes must:
 * - Be a non-null object
 * - Have a "type" property (usually "object")
 * - If type is "object", should have a "properties" property
 *
 * This is NOT a full JSON Schema meta-validation -- it checks the structural
 * conventions expected by MutagenT's prompt system.
 */
export function isValidJsonSchema(schema: unknown): boolean {
  if (schema === null || schema === undefined) return false;
  if (typeof schema !== 'object') return false;
  if (Array.isArray(schema)) return false;

  const obj = schema as Record<string, unknown>;

  // Must have "type" field
  if (!('type' in obj) || typeof obj.type !== 'string') return false;

  // If type is "object", should have "properties"
  if (obj.type === 'object') {
    if (!('properties' in obj) || typeof obj.properties !== 'object' || obj.properties === null) {
      return false;
    }
  }

  return true;
}

/**
 * Build a JSON Schema from an array of variable definitions.
 *
 * @example
 * ```typescript
 * const schema = buildSchemaFromVariables([
 *   { name: 'customer_name', type: 'string', description: 'The customer full name' },
 *   { name: 'age', type: 'number', description: 'Customer age' },
 * ]);
 * // Returns:
 * // {
 * //   type: "object",
 * //   properties: {
 * //     customer_name: { type: "string", description: "The customer full name" },
 * //     age: { type: "number", description: "Customer age" }
 * //   }
 * // }
 * ```
 */
export function buildSchemaFromVariables(variables: SchemaVariable[]): Record<string, unknown> {
  const properties: Record<string, Record<string, unknown>> = {};

  for (const variable of variables) {
    properties[variable.name] = {
      type: variable.type,
      description: variable.description,
    };
  }

  return {
    type: 'object',
    properties,
  };
}

/**
 * Return a formatted JSON Schema example string for display in CLI help output.
 */
export function formatSchemaExample(): string {
  return JSON.stringify(
    {
      type: 'object',
      properties: {
        variable_name: { type: 'string', description: 'Description here' },
      },
    },
    null,
    2,
  );
}

/**
 * Validate that all properties in a JSON schema have `description` fields.
 * Returns a list of property names missing descriptions.
 */
export function validateSchemaDescriptions(schema: unknown): string[] {
  if (schema === null || schema === undefined) return [];
  if (typeof schema !== 'object' || Array.isArray(schema)) return [];

  const obj = schema as Record<string, unknown>;
  const properties = obj.properties;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return [];

  const missing: string[] = [];
  const props = properties as Record<string, unknown>;
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || typeof value !== 'object') {
      missing.push(key);
      continue;
    }
    const prop = value as Record<string, unknown>;
    if (typeof prop.description !== 'string' || prop.description.trim() === '') {
      missing.push(key);
    }
  }

  return missing;
}

/**
 * Return a formatted warning message with schema example for invalid schemas.
 * Used by the create/update commands when inputSchema or outputSchema
 * does not look like valid JSON Schema.
 */
export function formatSchemaWarning(fieldName: string): string {
  return [
    `${fieldName} doesn't appear to be a valid JSON Schema.`,
    '',
    '  Expected format:',
    '  {',
    '    "type": "object",',
    '    "properties": {',
    '      "variable_name": { "type": "string", "description": "Description here" }',
    '    }',
    '  }',
    '',
    '  You can also provide a schema via --data: mutagent prompts create -d \'{"inputSchema":{...},"outputSchema":{...}}\'',
  ].join('\n');
}
